/**
 * Guardar el mazo.
 *
 * Es la única escritura directa que el cliente tiene permitida en todo el
 * proyecto, y solo porque un mazo no tiene valor económico: no crea cartas, solo
 * referencia las que ya posees. Que las referencias sean legales lo garantiza el
 * trigger `validate_deck` —20 cartas, todas tuyas, sin repetir ejemplar—, no la
 * interfaz. La interfaz solo se adelanta para no dejarte pulsar Guardar sabiendo
 * que la base lo va a rechazar.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryClient';
import { describeDatabaseError, requireSupabase } from '@/services/supabaseClient';
import { DECK_SIZE } from '@/types/game';

export interface SaveDeckInput {
  deckId: string;
  cardIds: string[];
}

/** Motivo por el que un mazo no es legal, o null si lo es. */
export function validateDeck(cardIds: readonly string[]): string | null {
  if (cardIds.length !== DECK_SIZE) {
    const diff = DECK_SIZE - cardIds.length;
    // El verbo concuerda igual que el sustantivo: "te falta 1 carta", no "te
    // faltan 1 carta". Es un detalle, pero un mensaje mal escrito hace dudar de
    // todo lo demás.
    const count = Math.abs(diff);
    const noun = `${count} carta${count === 1 ? '' : 's'}`;
    return diff > 0
      ? `Te falta${count === 1 ? '' : 'n'} ${noun} para completar el mazo.`
      : `Te sobra${count === 1 ? '' : 'n'} ${noun}.`;
  }
  if (new Set(cardIds).size !== cardIds.length) {
    return 'Hay un ejemplar repetido en el mazo.';
  }
  return null;
}

export function useSaveDeck(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deckId, cardIds }: SaveDeckInput): Promise<void> => {
      const invalid = validateDeck(cardIds);
      if (invalid) throw new Error(invalid);

      const { error } = await requireSupabase()
        .from('decks')
        .update({ card_ids: cardIds })
        .eq('id', deckId);

      if (error) {
        // El trigger devuelve mensajes ya escritos para una persona; los demás
        // errores de PostgREST no.
        throw new Error(
          error.code === '23514' || /mazo/i.test(error.message)
            ? error.message
            : describeDatabaseError(error),
        );
      }
    },
    onSuccess: () => {
      if (!userId) return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.activeDeck(userId) });
    },
  });
}
